import { BufferGeometry, Vector3 } from 'three';
import type { Rng } from '@cloudpearl/core';
import { MeshBuilder, cardCloud, mixRGB, rgb, scaleRGB, smoothSpine, smoothTube } from './builder';
import type { Clump, RGB, TubeRing } from './builder';

export interface PlantParts {
  wood?: BufferGeometry;
  leaves?: BufferGeometry;
}

/** Jittered polyline from `from` along `dir`, bending upward/outward. */
function limb(rng: Rng, from: Vector3, dir: Vector3, len: number, steps: number, wobble: number, lift = 0): Vector3[] {
  const pts = [from.clone()];
  const d = dir.clone().normalize();
  const p = from.clone();
  for (let i = 1; i <= steps; i++) {
    d.x += (rng.float() - 0.5) * wobble;
    d.z += (rng.float() - 0.5) * wobble;
    d.y += lift;
    d.normalize();
    p.addScaledVector(d, len / steps);
    pts.push(p.clone());
  }
  return pts;
}

function taper(spine: Vector3[], r0: number, r1: number, flare = 0): TubeRing[] {
  return spine.map((p, i) => {
    const t = i / (spine.length - 1);
    const fl = flare > 0 ? flare * Math.pow(Math.max(0, 1 - t * 6), 2) : 0;
    return { p, r: r0 + (r1 - r0) * Math.pow(t, 0.85) + fl };
  });
}

/**
 * Broadleaf tree. Smooth tapered trunk with a root flare, a few main limbs and sub-branches, and
 * ~1300 small billboard leaf cards in clumps at the branch tips. Unit-scale silhouette matches the
 * collider: trunk r≈0.45 to y≈3.4, canopy r≈2.3 from y≈3.2 to 9.5.
 */
export function roundTree(rng: Rng, bark: string): PlantParts {
  const mb = new MeshBuilder();
  const barkC = rgb(bark);
  const barkTop = mixRGB(barkC, rgb('#8a7a64'), 0.25);
  const trunkTop = rng.range(4.4, 5.0);
  const trunkCtl = [new Vector3(0, -0.6, 0)];
  for (let i = 1; i <= 4; i++) trunkCtl.push(new Vector3((rng.float() - 0.5) * 0.3, (i / 4) * trunkTop, (rng.float() - 0.5) * 0.3));
  const trunk = smoothSpine(trunkCtl, 4);
  smoothTube(mb, taper(trunk, 0.46, 0.2, 0.28), 14, { color: barkC, colorEnd: barkTop, rootAO: 0.45, jitter: 0.08, seed: rng.float() * 50, capEnd: true });

  const clumps: Clump[] = [];
  const top = trunk[trunk.length - 1]!;
  const limbs = rng.int(4, 5);
  const a0 = rng.float() * Math.PI * 2;
  for (let b = 0; b < limbs; b++) {
    const a = a0 + (b / limbs) * Math.PI * 2 + rng.range(-0.3, 0.3);
    const startI = Math.floor(trunk.length * rng.range(0.55, 0.85));
    const start = trunk[startI]!.clone();
    const dir = new Vector3(Math.cos(a), rng.range(0.7, 1.2), Math.sin(a));
    const len = rng.range(1.9, 2.6);
    const spine = smoothSpine(limb(rng, start, dir, len, 3, 0.35, 0.08), 3);
    smoothTube(mb, taper(spine, 0.17, 0.05), 7, { color: barkTop, jitter: 0.05, seed: b * 3.1, capEnd: true });
    const end = spine[spine.length - 1]!;
    // Sub-branch.
    const mid = spine[Math.floor(spine.length * 0.55)]!;
    const subDir = new Vector3(Math.cos(a + rng.range(0.6, 1.1) * rng.sign()), rng.range(0.5, 1.0), Math.sin(a + 0.8));
    const sub = smoothSpine(limb(rng, mid, subDir, rng.range(0.9, 1.3), 2, 0.4), 3);
    smoothTube(mb, taper(sub, 0.08, 0.025), 5, { color: barkTop, jitter: 0.04, seed: b * 7.7, capEnd: true });
    const subEnd = sub[sub.length - 1]!;
    clumps.push({ center: end.clone().add(new Vector3(0, 0.45, 0)), radius: new Vector3(1.25, 1.0, 1.25).multiplyScalar(rng.range(0.9, 1.1)), count: 240, cardSize: 0.225 });
    clumps.push({ center: subEnd.clone().add(new Vector3(0, 0.3, 0)), radius: new Vector3(0.85, 0.7, 0.85), count: 112, cardSize: 0.21 });
  }
  const crown = new Vector3(top.x, Math.max(top.y + 1.4, 6.2), top.z);
  clumps.push({ center: crown, radius: new Vector3(2.1, 1.75, 2.1), count: 610, cardSize: 0.24 });
  clumps.push({ center: crown.clone().add(new Vector3(rng.range(-0.4, 0.4), 1.9, rng.range(-0.4, 0.4))), radius: new Vector3(1.3, 1.0, 1.3), count: 225, cardSize: 0.225 });
  const leaves = cardCloud(clumps, rng, { treeCenter: crown.clone().add(new Vector3(0, -0.2, 0)), treeRadius: 2.6, aoFloor: 0.2 });
  return { wood: mb.build(), leaves };
}

/**
 * Conifer: straight smooth trunk and drooping tiers of small cards, narrowing to a spire.
 * Trunk r≈0.4, foliage r≈1.6 from y≈1.8 to 11.
 */
export function pineTree(rng: Rng, bark: string): PlantParts {
  const mb = new MeshBuilder();
  const barkC = rgb(bark);
  const h = rng.range(10.4, 11.0);
  const ctl: Vector3[] = [new Vector3(0, -0.6, 0)];
  for (let i = 1; i <= 5; i++) ctl.push(new Vector3((rng.float() - 0.5) * 0.12, (i / 5) * h, (rng.float() - 0.5) * 0.12));
  const trunk = smoothSpine(ctl, 3);
  smoothTube(mb, taper(trunk, 0.4, 0.05, 0.22), 12, { color: barkC, colorEnd: scaleRGB(barkC, 0.8), rootAO: 0.4, jitter: 0.06, seed: rng.float() * 40, capEnd: true });
  const clumps: Clump[] = [];
  const tiers = 9;
  for (let t = 0; t < tiers; t++) {
    const f = t / (tiers - 1);
    const y = 2.1 + f * (h - 2.4);
    const r = 1.7 * Math.pow(1 - f, 0.9) + 0.25;
    // Each tier: a ring of drooping sub-clumps around the trunk.
    const n = Math.max(1, Math.round(r * 3.2));
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + t * 0.7;
      const d = n === 1 ? 0 : r * 0.5;
      clumps.push({
        center: new Vector3(Math.cos(a) * d, y - r * 0.12, Math.sin(a) * d),
        radius: new Vector3(r * 0.62, 0.32 + r * 0.12, r * 0.62),
        count: Math.round(35 + r * 38),
        cardSize: 0.18 + r * 0.022,
      });
    }
  }
  return { wood: mb.build(), leaves: cardCloud(clumps, rng, { treeCenter: new Vector3(0, h * 0.45, 0), treeRadius: 2.2, aoFloor: 0.2 }) };
}

/** Low, dense leafy bush (no trunk). */
export function bush(rng: Rng): PlantParts {
  const clumps: Clump[] = [];
  const n = rng.int(3, 4);
  for (let i = 0; i < n; i++) {
    const a = rng.float() * Math.PI * 2;
    const d = i === 0 ? 0 : rng.range(0.5, 0.9);
    const r = rng.range(0.7, 1.0);
    clumps.push({ center: new Vector3(Math.cos(a) * d, r * 0.7, Math.sin(a) * d), radius: new Vector3(r, r * 0.78, r), count: 175, cardSize: 0.18 });
  }
  return { leaves: cardCloud(clumps, rng, { treeCenter: new Vector3(0, 0.5, 0), treeRadius: 1.4, aoFloor: 0.28 }) };
}

/** Twiggy dry desert shrub: thin smooth sticks + sparse dry leaf cards. */
export function dryBush(rng: Rng, bark: string): PlantParts {
  const mb = new MeshBuilder();
  const c = rgb(bark);
  const clumps: Clump[] = [];
  const n = rng.int(8, 12);
  for (let i = 0; i < n; i++) {
    const a = rng.float() * Math.PI * 2;
    const dir = new Vector3(Math.cos(a) * rng.range(0.5, 1), rng.range(0.7, 1.2), Math.sin(a) * rng.range(0.5, 1));
    const spine = smoothSpine(limb(rng, new Vector3(0, -0.1, 0), dir, rng.range(0.8, 1.3), 3, 0.6), 2);
    smoothTube(mb, taper(spine, 0.045, 0.012), 4, { color: c, jitter: 0.05, seed: i });
    if (rng.chance(0.75)) clumps.push({ center: spine[spine.length - 1]!, radius: new Vector3(0.3, 0.24, 0.3), count: 40, cardSize: 0.078 });
  }
  return { wood: mb.build(), leaves: clumps.length ? cardCloud(clumps, rng, { aoFloor: 0.5 }) : undefined };
}

/** Bare, twisted dead tree. Trunk r≈0.5, h≈5.8. */
export function deadTree(rng: Rng, bark: string): PlantParts {
  const mb = new MeshBuilder();
  const c = rgb(bark);
  const h = rng.range(5.2, 5.8);
  const ctl: Vector3[] = [new Vector3(0, -0.6, 0)];
  for (let i = 1; i <= 4; i++) ctl.push(new Vector3((rng.float() - 0.5) * 0.7, (i / 4) * h, (rng.float() - 0.5) * 0.7));
  const trunk = smoothSpine(ctl, 4);
  smoothTube(mb, taper(trunk, 0.5, 0.14, 0.3), 12, { color: c, colorEnd: mixRGB(c, rgb('#d8cbb8'), 0.3), rootAO: 0.35, jitter: 0.12, seed: rng.float() * 30, capEnd: true });
  const grow = (from: Vector3, dir: Vector3, len: number, r: number, depth: number, seed: number): void => {
    const spine = smoothSpine(limb(rng, from, dir, len, 3, 0.5, 0.05), 2);
    smoothTube(mb, taper(spine, r, r * 0.3), depth > 1 ? 7 : 5, { color: mixRGB(c, rgb('#d8cbb8'), 0.35), jitter: 0.08, seed, capEnd: true });
    if (depth > 0) {
      const end = spine[spine.length - 1]!;
      for (let k = 0; k < 2; k++) {
        const nd = dir.clone().add(new Vector3((rng.float() - 0.5) * 1.4, rng.range(0.1, 0.6), (rng.float() - 0.5) * 1.4)).normalize();
        grow(end, nd, len * 0.6, r * 0.55, depth - 1, seed * 1.7 + k);
      }
    }
  };
  for (let b = 0; b < 3; b++) {
    const a = rng.float() * Math.PI * 2;
    const from = trunk[Math.floor(trunk.length * rng.range(0.45, 0.8))]!;
    grow(from, new Vector3(Math.cos(a), rng.range(0.5, 1.0), Math.sin(a)), rng.range(1.4, 2.0), 0.17, 2, b + 1);
  }
  return { wood: mb.build() };
}

/** Saguaro: smooth ribbed trunk r≈0.45 h≈4.6 with arms reaching ≤1.25 out between y 1.6–3.4. */
export function cactus(rng: Rng, green: string): PlantParts {
  const mb = new MeshBuilder();
  const c: RGB = rgb(green);
  const tip = mixRGB(c, rgb('#b8c070'), 0.25);
  const h = rng.range(4.1, 4.6);
  const rings: TubeRing[] = [{ p: new Vector3(0, -0.4, 0), r: 0.46 }];
  for (let i = 0; i <= 8; i++) rings.push({ p: new Vector3(0, (i / 8) * (h - 0.5), 0), r: 0.45 });
  for (let i = 1; i <= 5; i++) {
    const t = i / 5;
    rings.push({ p: new Vector3(0, h - 0.5 + t * 0.5, 0), r: 0.45 * Math.sqrt(Math.max(0.02, 1 - t * t)) });
  }
  smoothTube(mb, rings, 24, { color: c, colorEnd: tip, ribs: 12, ribDepth: 0.07, jitter: 0.01, rootAO: 0.3 });
  const arms = rng.int(1, 2);
  const side0 = rng.sign();
  for (let a = 0; a < arms; a++) {
    const side = a === 0 ? side0 : -side0;
    const ang = rng.range(-0.5, 0.5);
    const dir = new Vector3(Math.cos(ang) * side, 0, Math.sin(ang));
    const y0 = rng.range(1.6, 2.2);
    const out = rng.range(0.85, 0.95);
    const top = rng.range(3.0, 3.4);
    const ctl = [
      new Vector3(0, y0, 0),
      new Vector3(0, y0, 0).addScaledVector(dir, out * 0.6).add(new Vector3(0, 0.02, 0)),
      new Vector3(0, y0, 0).addScaledVector(dir, out).add(new Vector3(0, 0.45, 0)),
      new Vector3(0, top - 0.25, 0).addScaledVector(dir, out),
    ];
    const spine = smoothSpine(ctl, 4);
    const armRings: TubeRing[] = spine.map((p) => ({ p, r: 0.26 }));
    const end = spine[spine.length - 1]!;
    for (let i = 1; i <= 4; i++) {
      const t = i / 4;
      armRings.push({ p: end.clone().add(new Vector3(0, t * 0.26, 0)), r: 0.26 * Math.sqrt(Math.max(0.02, 1 - t * t)) });
    }
    smoothTube(mb, armRings, 16, { color: c, colorEnd: tip, ribs: 10, ribDepth: 0.07, jitter: 0.01 });
  }
  return { wood: mb.build() };
}

/**
 * Desert grass tuft: ~40 curved, tapered blades fanning out from a point. Vertex colour runs from
 * a dark base to a sun-bleached tip; normals point up-and-out for soft, rounded shading.
 */
export function grassTuft(rng: Rng, base: string, tip: string): BufferGeometry {
  const mb = new MeshBuilder();
  const cb = rgb(base);
  const ct = rgb(tip);
  const blades = rng.int(34, 46);
  const p = new Vector3();
  const n = new Vector3();
  for (let b = 0; b < blades; b++) {
    const a = rng.float() * Math.PI * 2;
    const lean = rng.range(0.15, 0.75);
    const h = rng.range(0.45, 0.95) * (1 - lean * 0.3);
    const w = rng.range(0.025, 0.04);
    const dx = Math.cos(a);
    const dz = Math.sin(a);
    const px = -dz;
    const pz = dx;
    const segs = 4;
    const o = new Vector3((rng.float() - 0.5) * 0.15, 0, (rng.float() - 0.5) * 0.15);
    const start = mb.vertexCount;
    for (let s = 0; s <= segs; s++) {
      const t = s / segs;
      const out = lean * t * t * h * 1.2;
      const y = h * t * (1 - lean * 0.35 * t);
      const ww = w * (1 - t * 0.92);
      const col = mixRGB(cb, ct, Math.pow(t, 0.8));
      n.set(dx * 0.5, 1, dz * 0.5).normalize();
      for (const side of [-1, 1]) {
        p.set(o.x + dx * out + px * ww * side, y - 0.05, o.z + dz * out + pz * ww * side);
        mb.vertex(p, n, col, (side + 1) / 2, t, t);
      }
    }
    for (let s = 0; s < segs; s++) {
      const i = start + s * 2;
      mb.tri(i, i + 1, i + 3);
      mb.tri(i, i + 3, i + 2);
    }
  }
  return mb.build();
}
